import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { cn } from "../../../lib/utils";

/**
 * Full-size lightbox for message photos, with prev/next when there are several.
 *
 * @param {Object} props
 * @param {string[]} props.urls
 * @param {number} props.index
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(index: number) => void} props.onIndexChange
 * @returns {JSX.Element}
 */
function MessageImageLightbox({ urls, index, open, onOpenChange, onIndexChange }) {
  const count = urls.length;
  const current = count ? Math.min(Math.max(index, 0), count - 1) : 0;
  const url = urls[current] || "";

  useEffect(() => {
    if (!open || count < 2) return undefined;

    /**
     * @param {KeyboardEvent} event
     */
    function onKeyDown(event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onIndexChange((current - 1 + count) % count);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onIndexChange((current + 1) % count);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, count, current, onIndexChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(100vw-2rem,56rem)] border-0 bg-transparent p-0 shadow-none">
        <DialogHeader className="sr-only">
          <DialogTitle>
            {count > 1 ? `Photo ${current + 1} of ${count}` : "Photo"}
          </DialogTitle>
        </DialogHeader>
        <div className="relative flex items-center justify-center">
          <DialogClose
            className="absolute right-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-carousel-control text-carousel-control-fg shadow-shell-sm"
            aria-label="Close photo"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </DialogClose>
          {url ? (
            <img
              src={url}
              alt={count > 1 ? `Photo ${current + 1} of ${count}` : "Photo"}
              className="max-h-[85vh] max-w-full rounded-md object-contain"
            />
          ) : null}
          {count > 1 ? (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={() => onIndexChange((current - 1 + count) % count)}
                className="absolute left-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-carousel-control text-carousel-control-fg shadow-shell-sm"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={() => onIndexChange((current + 1) % count)}
                className="absolute right-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-carousel-control text-carousel-control-fg shadow-shell-sm"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline thumbnails for photos on a chat message. Click opens a lightbox.
 *
 * @param {Object} props
 * @param {string[]} props.urls
 * @returns {JSX.Element | null}
 */
function MessageImages({ urls }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  if (!urls?.length) return null;

  const isSingle = urls.length === 1;

  return (
    <>
      <div
        className={cn(
          "grid w-56 gap-1",
          isSingle ? "grid-cols-1" : "grid-cols-2"
        )}
      >
        {urls.map((url, imageIndex) => (
          <button
            key={`${url}-${imageIndex}`}
            type="button"
            onClick={() => {
              setIndex(imageIndex);
              setOpen(true);
            }}
            className={cn(
              "overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring",
              isSingle ? "max-h-64" : "aspect-square"
            )}
            aria-label={
              urls.length > 1
                ? `View photo ${imageIndex + 1} of ${urls.length}`
                : "View photo"
            }
          >
            <img
              src={url}
              alt=""
              className={cn(
                "h-full w-full object-cover",
                isSingle && "max-h-64"
              )}
            />
          </button>
        ))}
      </div>
      <MessageImageLightbox
        urls={urls}
        index={index}
        open={open}
        onOpenChange={setOpen}
        onIndexChange={setIndex}
      />
    </>
  );
}

export default MessageImages;
